import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";
import { useEffect, useState } from "react";
import { fallback, formatDate } from "../utils/formatters";
import apiService from "../../../service/Api";
import { useNavigate } from "react-router-dom";

interface Booking {
  id: number;
  refferal: {
    id: number;
    name: string;
    surname: string;
    email: string;
    status: string;
    commission_rate: number;
    phone_number: string;
  };
  booking: {
    id: number;
    title: string;
    status: string;
    total: string;
    currency: string;
    payment_status: string;
    source_of_booking: string;
    booking_type: string;
    created_at: string;
    deceased: {
      id: number;
      first_name: string;
      surname: string;
      date_of_birth: string;
      date_of_death: string;
    }[];
    package_item: {
      id: number;
      name: string;
      price: string;
    } | null;
  };
  commission_status: string;
  commission_rate_applied: string;
  commission_amount: string;
  reward_paid: boolean;
  reward_paid_at: string | null;
  created_at: string;
}

interface Props {
  referralId: string | number;
}

const getStatusStyles = (status?: string | null) => {
  const normalized = (status || "").toLowerCase();

  if (normalized === "upcoming") {
    return { backgroundColor: "#E8F5E9", color: "#2E7D32", label: "Upcoming" };
  }
  if (normalized === "completed") {
    return { backgroundColor: "#E3F2FD", color: "#1565C0", label: "Completed" };
  }
  if (normalized === "cancelled") {
    return { backgroundColor: "#FFEBEE", color: "#C62828", label: "Cancelled" };
  }
  if (normalized === "enquiry") {
    return { backgroundColor: "#FFF8E1", color: "#F57F17", label: "Enquiry" };
  }
  if (normalized === "pending") {
    return { backgroundColor: "#FFF3E0", color: "#E65100", label: "Pending" };
  }

  return {
    backgroundColor: "#EEF2F6",
    color: "#55657D",
    label: status || "N/A",
  };
};

const getCommissionStatusStyles = (status?: string | null) => {
  const normalized = (status || "").toLowerCase();
  if (normalized === "paid") {
    return { backgroundColor: "#E8F5E9", color: "#2E7D32", label: "Paid" };
  }
  if (normalized === "pending") {
    return { backgroundColor: "#FFF3E0", color: "#E65100", label: "Pending" };
  }
  return {
    backgroundColor: "#EEF2F6",
    color: "#55657D",
    label: status || "N/A",
  };
};

export default function ReferralsTable({ referralId }: Props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  useEffect(() => {
    if (!referralId) return;

    const fetchBookings = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await apiService.get<{ results: Booking[] }>(
          `/api/refferal-program/refferal-bookings/?refferal=${referralId}`,
        );
        setBookings(response.data.results ?? []);
      } catch {
        setError("Failed to load bookings.");
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [referralId]);

  const upcoming = bookings.filter(
    (b) => b.booking?.status?.toLowerCase() === "upcoming",
  ).length;
  const completed = bookings.filter(
    (b) => b.booking?.status?.toLowerCase() === "completed",
  ).length;
  const cancelled = bookings.filter(
    (b) => b.booking?.status?.toLowerCase() === "cancelled",
  ).length;

  return (
    <Paper
      elevation={0}
      sx={{ borderRadius: "20px", p: 2.5, backgroundColor: colors.primary[900] }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          alignItems: "center",
          mb: 2.5,
          flexWrap: "wrap",
        }}
      >
        <Typography sx={{ fontSize: "1rem", fontWeight: 600 }}>
          Referral Bookings
        </Typography>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip
            label={`Upcoming ${upcoming}`}
            sx={{
              borderRadius: "10px",
              backgroundColor: "#7B5648",
              color: "#fff",
            }}
          />
          <Chip
            label={`Completed ${completed}`}
            sx={{ borderRadius: "10px", backgroundColor: "#F3F5F8" }}
          />
          <Chip
            label={`Cancelled ${cancelled}`}
            sx={{ borderRadius: "10px", backgroundColor: "#F3F5F8" }}
          />
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Box
          sx={{
            borderRadius: "16px",
            p: 3,
            textAlign: "center",
            color: "error.main",
          }}
        >
          {error}
        </Box>
      ) : bookings.length === 0 ? (
        <Box
          sx={{
            borderRadius: "16px",
            p: 3,
            textAlign: "center",
            color: "#8A94A6",
          }}
        >
          No referral records available at the moment
        </Box>
      ) : (
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#F3F5F8" }}>
              <TableCell>BOOKING ID</TableCell>
              <TableCell>DECEASED</TableCell>
              <TableCell>DATE</TableCell>
              <TableCell>PACKAGE</TableCell>
              <TableCell>BOOKING STATUS</TableCell>
              <TableCell>TOTAL</TableCell>
              <TableCell>COMMISSION</TableCell>
              <TableCell>COMMISSION STATUS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bookings.map((item) => {
              const deceased = item.booking?.deceased?.[0];
              const bookingStatus = getStatusStyles(item.booking?.status);
              const commissionStatus = getCommissionStatusStyles(
                item.commission_status,
              );

              return (
                <TableRow
                  key={item.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate(`/bookings/${item.booking?.id}`)}
                >
                  <TableCell>
                    {fallback(item.booking?.id?.toString())}
                  </TableCell>
                  <TableCell>
                    {deceased
                      ? `${deceased.first_name} ${deceased.surname}`
                      : "—"}
                  </TableCell>
                  <TableCell>{formatDate(item.created_at)}</TableCell>
                  <TableCell>
                    {fallback(item.booking?.package_item?.name)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={bookingStatus.label}
                      sx={{
                        backgroundColor: bookingStatus.backgroundColor,
                        color: bookingStatus.color,
                        borderRadius: "999px",
                        fontSize: "0.75rem",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {item.booking?.total
                      ? `$${parseFloat(item.booking.total).toFixed(2)}`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {item.commission_amount
                      ? `$${parseFloat(item.commission_amount).toFixed(2)}`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={commissionStatus.label}
                      sx={{
                        backgroundColor: commissionStatus.backgroundColor,
                        color: commissionStatus.color,
                        borderRadius: "999px",
                        fontSize: "0.75rem",
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}
